import { createSlice } from "@reduxjs/toolkit";
import { readScopedJson, removeScopedStorage, writeScopedJson } from "../util/privacyConsent";

const STORAGE_KEY = "checkout_state";

const load = () => {
  try {
    const scoped = readScopedJson(STORAGE_KEY, null, { persistWhenAccepted: false });
    if (scoped) return scoped;
    if (typeof window === "undefined") return null;
    const legacy = window.localStorage.getItem(STORAGE_KEY);
    if (!legacy) return null;
    const parsed = JSON.parse(legacy);
    writeScopedJson(STORAGE_KEY, parsed, { persistWhenAccepted: false });
    window.localStorage.removeItem(STORAGE_KEY);
    return parsed;
  } catch {
    return null;
  }
};

const save = (state) => {
  try {
    const toSave = {
      shippingAddress: state.shippingAddress,
      billingAddress: state.billingAddress,
      billingSameAsShipping: state.billingSameAsShipping,
      paymentMethod: state.paymentMethod,
      paymentGatewayLabel: state.paymentGatewayLabel,
      region: state.region,
      billingCountry: state.billingCountry,
      transactionCurrency: state.transactionCurrency,
      baseCurrency: state.baseCurrency,
      exchangeRate: state.exchangeRate,
      exchangeRateSnapshot: state.exchangeRateSnapshot,
      voucher: state.voucher,
    };
    writeScopedJson(STORAGE_KEY, toSave, { persistWhenAccepted: false });
  } catch {
    // ignore
  }
};

const saved = load();

const initialState = {
  shippingAddress: saved?.shippingAddress || null,
  billingAddress: saved?.billingAddress || null,
  billingSameAsShipping:
    saved?.billingSameAsShipping !== undefined ? saved.billingSameAsShipping : true,
  paymentMethod: saved?.paymentMethod || null,
  paymentGatewayLabel: saved?.paymentGatewayLabel || "",
  // Payment-region selector (BD | US | INTL) — not server-derived; consumers
  // (OrderForm, ReviewOrder) fall back locally and OrderForm persists a concrete
  // value on mount, so an unset region never reaches the server.
  region: saved?.region || "",
  billingCountry: saved?.billingCountry || "",
  transactionCurrency: saved?.transactionCurrency || "",
  baseCurrency: saved?.baseCurrency || "USD",
  exchangeRate: Number(saved?.exchangeRate || 0) || null,
  exchangeRateSnapshot: saved?.exchangeRateSnapshot || null,
  voucher: saved?.voucher || null,
};

const clearCurrencyContext = (state) => {
  state.billingCountry = "";
  state.transactionCurrency = "";
  state.baseCurrency = "USD";
  state.exchangeRate = null;
  state.exchangeRateSnapshot = null;
};

const checkoutSlice = createSlice({
  name: "checkout",
  initialState,
  reducers: {
    setShippingAddress(state, action) {
      const previousCountry = String(state.shippingAddress?.countryCode || "").toUpperCase();
      const nextCountry = String(action.payload?.countryCode || "").toUpperCase();
      state.shippingAddress = action.payload;
      const voucherCountry = String(state.voucher?.shippingCountry || "").toUpperCase();
      if (state.voucher && (!voucherCountry || (nextCountry && voucherCountry !== nextCountry))) {
        state.voucher = null;
      }
      if (state.billingSameAsShipping) {
        state.billingAddress = action.payload;
        if (
          previousCountry !== nextCountry &&
          nextCountry !== String(state.billingCountry || "").toUpperCase()
        ) {
          clearCurrencyContext(state);
        }
      }
      save(state);
    },
    setBillingSameAsShipping(state, action) {
      state.billingSameAsShipping = Boolean(action.payload);
      if (state.billingSameAsShipping) {
        state.billingAddress = state.shippingAddress;
        clearCurrencyContext(state);
      }
      save(state);
    },
    setBillingAddress(state, action) {
      const previousCountry = String(state.billingAddress?.countryCode || "").toUpperCase();
      const nextCountry = String(action.payload?.countryCode || "").toUpperCase();
      state.billingAddress = action.payload;
      if (
        previousCountry !== nextCountry &&
        nextCountry !== String(state.billingCountry || "").toUpperCase()
      ) {
        clearCurrencyContext(state);
      }
      save(state);
    },
    setPaymentMethod(state, action) {
      state.paymentMethod = action.payload;
      save(state);
    },
    setPaymentGatewayLabel(state, action) {
      state.paymentGatewayLabel = action.payload || "";
      save(state);
    },
    setRegion(state, action) {
      state.region = action.payload;
      save(state);
    },
    setTransactionCurrencyContext(state, action) {
      const context = action.payload || {};
      state.billingCountry = String(context.billingCountry || "").trim().toUpperCase();
      state.transactionCurrency = String(context.transactionCurrency || "").trim().toUpperCase();
      state.baseCurrency = String(context.baseCurrency || "USD").trim().toUpperCase();
      state.exchangeRate = Number(context.exchangeRate || 0) || null;
      state.exchangeRateSnapshot = context.exchangeRateSnapshot || null;
      save(state);
    },
    clearTransactionCurrencyContext(state) {
      clearCurrencyContext(state);
      save(state);
    },
    setCheckoutVoucher(state, action) {
      state.voucher = action.payload || null;
      save(state);
    },
    clearCheckout(state) {
      state.shippingAddress = null;
      state.billingAddress = null;
      state.billingSameAsShipping = true;
      state.paymentMethod = null;
      state.paymentGatewayLabel = "";
      state.region = "";
      clearCurrencyContext(state);
      state.voucher = null;
      removeScopedStorage(STORAGE_KEY);
    },
  },
});

export const {
  setShippingAddress,
  setBillingSameAsShipping,
  setBillingAddress,
  setPaymentMethod,
  setPaymentGatewayLabel,
  setRegion,
  setTransactionCurrencyContext,
  clearTransactionCurrencyContext,
  setCheckoutVoucher,
  clearCheckout,
} = checkoutSlice.actions;

export default checkoutSlice.reducer;
